import { take, call, put, fork } from 'redux-saga/effects';
import { get } from 'lodash';

import { ApiClient } from '@shared/services';
import { getBrowserDetails } from '@shared/utils';

import { UserTokenType } from '@/types';
import Constants from '@/utils/constants';
import { setAuthFlowGarfield } from '@/utils/GarfieldUtils';
import { getFingerprintCookie, refreshFingerprintToken } from '@/utils/helper';
import { INIT_APP_ERROR } from '@/redux/ducks/init/types';
import {
	LOGIN,
	LOGIN_SUCCESS,
	REGISTER_SUCCESS,
	LOGIN_ERROR,
	LOGOUT,
	REQUEST_OTP,
	REQUEST_OTP_ERROR,
	REQUEST_OTP_SUCCESS,
	RESENT_OTP,
	FORGOT_PASSWORD,
	FORGOT_PASSWORD_ERROR,
	FORGOT_PASSWORD_SUCCESS,
	CHANGE_PASSWORD,
	CHANGE_PASSWORD_ERROR,
	CHANGE_PASSWORD_SUCCESS,
	VERIFY_USER,
	VERIFY_USER_SUCCESS,
	VERIFY_USER_ERROR,
	SET_CAPTCHA_TOKEN,
	LOGOUT_CLEANUP,
} from '@/redux/ducks/login/types';
import { SET_USER_ROLE } from '@/redux/ducks/userProfile/types';
import api from '@/data';
import { saveData, removeData } from '@/data/cache';
import { NOTIFICATIONS_CLEANUP } from '@/redux/ducks/notifications/types';
import { pfbAnalyticsClient } from '@/data/analyticsClient';
import router from '@/router/router';

import { userInfoWatcher, clearStorage, getUserGroupList } from './userApiSaga';

export const getUserToken = (state) => state.userAuth.user.token;

export function* loginSuccess(response) {
	const authType = response.data.prefix.startsWith('O')
		? UserTokenType.OLYMPUS_TOKEN
		: UserTokenType.GANDALF_TOKEN;
	const groupSelection = response.data.groupSelection;
	yield call(saveData, Constants.LOGIN_CHECK_KEY, {
		isLoggedIn: true,
		authType,
		groupSelection,
	});
	yield put({
		type: LOGIN_SUCCESS,
		data: response.data,
	});
	yield put({
		type: SET_USER_ROLE,
		data: {
			roleName: get(response, 'data.roleName'),
			roleId: get(response, 'data.roleId'),
		},
	});
	yield call(getUserGroupList);
	setAuthFlowGarfield(true);
}

/**
 * Call login APIs and all other APIs on success.
 * @params
 * email- string,
 * password- string
 */
export function* loginFlow(payload, captcha) {
	try {
		yield call(api.removeAuthCookies);
		const browserFingerPrint = yield call(getFingerprintCookie);
		const metadata = {
			deviceInfo: {
				userAgent: navigator.userAgent,
				channelType: 'WEB',
				browserFingerPrint: {
					xosv: getBrowserDetails(),
					omid: 'Web Browser',
					xdpi: browserFingerPrint,
				},
				browserFingerPrintXosv: getBrowserDetails(),
				browserFingerPrintOmid: 'Web Browser',
				browserFingerPrintXdpi: browserFingerPrint,
			},
		};
		const response = yield call(
			api.login,
			{ loginRequest: payload, ...metadata },
			captcha,
		);
		if (response.data.expiry && response.data.token) {
			yield put({
				type: LOGIN_SUCCESS,
				data: response.data,
				endpoint: payload.username,
			});
			window.hcaptcha.reset();
			yield put({
				type: SET_CAPTCHA_TOKEN,
				value: '',
			});
		} else {
			yield call(loginSuccess, response);
		}
		yield put({ type: NOTIFICATIONS_CLEANUP });
	} catch (error) {
		yield put({
			type: LOGIN_ERROR,
			error: error.data,
		});
		window.hcaptcha.reset();
		yield put({
			type: SET_CAPTCHA_TOKEN,
			value: '',
		});
	}
}

/* Clear the localstorage and redirect user to /login */
export function* logoutTask() {
	pfbAnalyticsClient.flush();
	yield call(clearStorage); // remove all keys from localstorage
	yield call(removeData, Constants.LOGIN_CHECK_KEY);
	yield call(removeData, Constants.USER_GROUPS_LIST_KEY);
	yield call(removeData, Constants.SELECTED_MID);
	refreshFingerprintToken();
	setAuthFlowGarfield(false);
	ApiClient.reset();
	yield call(() => router.navigate('/login'));
}

export function* loginWatcher() {
	while (true) {
		const { payload, captcha } = yield take(LOGIN);
		yield fork(loginFlow, payload, captcha);
	}
}

export function* registerSuccessWatcher() {
	const { payload } = yield take(REGISTER_SUCCESS);
	yield call(loginSuccess, payload);
}

// Function to watch login error and init error. if there is any log out the user.
export function* initAppErrorWatcher() {
	while (true) {
		yield take([INIT_APP_ERROR, LOGIN_ERROR]); // watch for error
		yield call(logoutTask);
	}
}

/** Function to request otp while login via mobile number.
 * @params
 * phoneNumber- user phone number
 */

export function* requestOtp(phoneNumber, captcha, channelType) {
	try {
		yield call(api.removeAuthCookies);
		const response = yield call(
			api.otpLoginInitiate,
			phoneNumber,
			captcha,
			channelType,
		);
		yield put({
			type: REQUEST_OTP_SUCCESS,
			data: response.data, // save otp token
			phoneNumber,
		});
		window.hcaptcha.reset();
		yield put({
			type: SET_CAPTCHA_TOKEN,
			value: '',
		});
	} catch (error) {
		yield put({
			type: REQUEST_OTP_ERROR,
			error: error.data,
		});
		window.hcaptcha.reset();
		yield put({
			type: SET_CAPTCHA_TOKEN,
			value: '',
		});
	}
}

/** Function to get forgot password link in mail.
 * @param
 * email- string
 */
export function* getForgotPasswordLink(username, captcha) {
	try {
		yield call(api.removeAuthCookies);
		const response = yield call(api.forgotPassword, { username }, captcha);
		yield put({
			type: FORGOT_PASSWORD_SUCCESS, // set key to check success
			data: response.data.data,
		});
	} catch (error) {
		yield put({
			type: FORGOT_PASSWORD_ERROR,
			error: error.data,
		});
	}
}

/** Function to change password
 * @params
 * password- string
 * confirmPassword- string
 * captcha- string  (true, false)
 * token- string    Description- obtained from the set password link
 * type- string     Description- obtained from the set password link
 */
export function* changePassword(
	password,
	confirmPassword,
	captcha,
	token,
	type,
) {
	try {
		yield call(api.removeAuthCookies);
		const payload = {
			password,
			confirmPassword,
			token,
			type,
		};
		const response = yield call(api.setPassword, payload, captcha);
		yield put({
			type: CHANGE_PASSWORD_SUCCESS,
			data: response.data.data,
		});
	} catch (error) {
		yield put({
			type: CHANGE_PASSWORD_ERROR,
			error: error.data,
		});

		if (captcha !== 'true') {
			window.hcaptcha.reset();
			yield put({
				type: SET_CAPTCHA_TOKEN,
				value: '',
			});
		}
	}
}

export function* userVerify(verificationKey) {
	try {
		yield call(api.removeAuthCookies);
		const response = yield call(api.verifyUser, verificationKey);
		yield put({
			type: VERIFY_USER_SUCCESS,
			data: response.data,
		});
	} catch (error) {
		yield put({
			type: VERIFY_USER_ERROR,
			error: error.data,
		});
	}
}

export function* requestOtpWatcher() {
	while (true) {
		const {
			payload: { user, captcha, channelType },
		} = yield take(REQUEST_OTP);
		yield fork(requestOtp, user, captcha, channelType);
	}
}

export function* resentOtpWatcher() {
	while (true) {
		const {
			payload: { user },
		} = yield take(RESENT_OTP);
		yield fork(requestOtp, user);
	}
}

export function* forgotPasswordWatcher() {
	while (true) {
		const {
			payload: { email, captcha },
		} = yield take(FORGOT_PASSWORD);
		yield fork(getForgotPasswordLink, email, captcha);
	}
}

export function* changePasswordWatcher() {
	while (true) {
		const {
			payload: { password, confirmPassword, captcha, token, type },
		} = yield take(CHANGE_PASSWORD);
		yield fork(changePassword, password, confirmPassword, captcha, token, type);
	}
}

export function* userVerifyWatcher() {
	while (true) {
		const { verificationKey } = yield take(VERIFY_USER);
		yield fork(userVerify, verificationKey);
	}
}

export function* logoutWatcher() {
	while (true) {
		yield take(LOGOUT);
		try {
			yield call(logoutTask); // reset all storage to initial value
			yield put({ type: LOGOUT_CLEANUP });
			yield call(api.logout); // logout api call
		} catch (error) {
			// handle error on logout failure
			console.log(error);
		}
	}
}

function* loginFlowSaga() {
	yield fork(loginWatcher);
	yield fork(registerSuccessWatcher);
	yield fork(initAppErrorWatcher);
	yield fork(requestOtpWatcher);
	yield fork(resentOtpWatcher);
	yield fork(logoutWatcher);
	yield fork(userInfoWatcher);
	yield fork(forgotPasswordWatcher);
	yield fork(changePasswordWatcher);
	yield fork(userVerifyWatcher);
}

export default loginFlowSaga;
